import { useState } from 'react';

import { Checkbox } from '@/components/ui/Checkbox';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { SettingsSection } from '@/features/settings/settings-section';

import { teamApis } from '../team.query';
import { ChatModeEnum, type ChatMode } from '../team.types';

const CHAT_MODE_OPTIONS: {
  mode: ChatMode;
  label: string;
  helper?: string;
}[] = [
  { mode: 'lite', label: 'Quick' },
  { mode: 'regular', label: 'Default', helper: 'Always allowed' },
  { mode: 'max', label: 'Max' },
  { mode: 'ultra', label: 'Ultra', helper: 'Pro tier members only' },
];

type TeamChatModesCardProps = {
  teamId: string;
  allowedModes: ChatMode[] | null;
};

export const TeamChatModesCard = ({
  teamId,
  allowedModes,
}: TeamChatModesCardProps) => {
  const [saving, setSaving] = useState(false);
  const enabledModes: readonly ChatMode[] =
    allowedModes ?? ChatModeEnum.options;

  const setModeAllowed = async (mode: ChatMode, allowed: boolean) => {
    setSaving(true);
    try {
      await teamApis.updateDataControls(teamId, {
        allowed_chat_modes: ChatModeEnum.options.filter((option) =>
          option === mode ? allowed : enabledModes.includes(option)
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <YStack className="w-full gap-3">
      <SettingsSection.GroupHeader>Chat Modes</SettingsSection.GroupHeader>
      <SettingsSection.Group>
        <SettingsSection.Item direction="vertical">
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemDescription>
              Team members can only use the chat modes checked here.
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>

          <YStack className="w-full gap-2 pt-4">
            {CHAT_MODE_OPTIONS.map(({ mode, label, helper }) => (
              <XStack key={mode} className="items-center gap-2">
                <Checkbox
                  id={`team-chat-mode-${mode}`}
                  checked={enabledModes.includes(mode)}
                  disabled={mode === 'regular' || saving}
                  onCheckedChange={(checked) =>
                    void setModeAllowed(mode, checked === true)
                  }
                />
                <label
                  htmlFor={`team-chat-mode-${mode}`}
                  className="cursor-pointer select-none text-sm"
                >
                  {label}
                </label>
                {helper && (
                  <span className="text-xs text-foreground-muted">
                    {helper}
                  </span>
                )}
              </XStack>
            ))}
          </YStack>
        </SettingsSection.Item>
      </SettingsSection.Group>
    </YStack>
  );
};
